/* Mayan Tzolk'in tag emission.
   Signal layer separate from the display copy in starChartMayanContent.js.
   Day-sign keys are seeded from each sign's `gifts` array but rewritten as
   semantic snake_case (the gifts text is user-facing copy, not signal keys).
   Numbers say how strongly the sign comes through (traditional K'iche'
   reading: gentle, middle, strong), so they are `latent` only.

   Provenance carried in source_module ("star_chart_mayan"); collector dedups
   by tag_key|tag_type for the (user_id, tag_key, tag_type) upsert. */

// ── Day signs (20) ─────────────────────────────────────────────────────────
export const MAYAN_DAY_SIGN_TAGS = {
  imix: [{ key: "strong_intuition", type: "offers" }, { key: "original_ideas", type: "offers" }, { key: "feels_moods", type: "latent" }, { key: "easily_overwhelmed", type: "latent" }],
  ik: [{ key: "way_with_words", type: "offers" }, { key: "quick_mind", type: "offers" }, { key: "restless_spirit", type: "latent" }, { key: "quick_temper", type: "latent" }],
  akbal: [{ key: "fresh_starts", type: "offers" }, { key: "steady_hope", type: "offers" }, { key: "rich_inner_life", type: "latent" }, { key: "keeps_secrets", type: "latent" }],
  kan: [{ key: "holds_people_together", type: "offers" }, { key: "sees_connections", type: "offers" }, { key: "resourceful", type: "latent" }, { key: "gets_entangled", type: "latent" }],
  chikchan: [{ key: "inner_strength", type: "offers" }, { key: "sense_of_justice", type: "offers" }, { key: "sharp_perception", type: "latent" }, { key: "forceful", type: "latent" }],
  kimi: [{ key: "calm_in_hard_times", type: "offers" }, { key: "wisdom_about_endings", type: "offers" }, { key: "honors_ancestors", type: "latent" }, { key: "dwells_on_loss", type: "latent" }],
  manik: [{ key: "natural_leadership", type: "offers" }, { key: "protective_strength", type: "offers" }, { key: "steadiness", type: "latent" }, { key: "carries_too_much", type: "latent" }],
  lamat: [{ key: "makes_things_grow", type: "offers" }, { key: "generous", type: "offers" }, { key: "patient_with_slow_things", type: "latent" }, { key: "scarcity_worry", type: "latent" }],
  muluk: [{ key: "gratitude", type: "offers" }, { key: "fairness", type: "offers" }, { key: "generous_giving", type: "latent" }, { key: "keeps_score", type: "latent" }],
  ok: [{ key: "loyalty", type: "offers" }, { key: "sense_of_justice", type: "offers" }, { key: "reliability", type: "latent" }, { key: "rule_bound", type: "latent" }],
  chuwen: [{ key: "creativity", type: "offers" }, { key: "craft_skill", type: "offers" }, { key: "playful_spirit", type: "latent" }, { key: "starts_more_than_finishes", type: "latent" }],
  eb: [{ key: "sense_of_direction", type: "offers" }, { key: "guides_others", type: "offers" }, { key: "openness", type: "latent" }, { key: "restless_traveler", type: "latent" }],
  ben: [{ key: "makes_a_home", type: "offers" }, { key: "dependability", type: "offers" }, { key: "care_for_family", type: "latent" }, { key: "rigid_about_how", type: "latent" }],
  ix: [{ key: "quiet_power", type: "offers" }, { key: "fierce_protection", type: "offers" }, { key: "bond_with_nature", type: "latent" }, { key: "guarded", type: "latent" }],
  men: [{ key: "brings_people_together", type: "offers" }, { key: "wide_vision", type: "offers" }, { key: "good_fortune", type: "latent" }, { key: "chases_luck", type: "latent" }],
  kib: [{ key: "forgiveness", type: "offers" }, { key: "understands_people", type: "offers" }, { key: "breaks_old_patterns", type: "latent" }, { key: "hard_on_self", type: "latent" }],
  kaban: [{ key: "good_counsel", type: "offers" }, { key: "clear_thinking", type: "offers" }, { key: "long_memory", type: "latent" }, { key: "overthinks", type: "latent" }],
  etznab: [{ key: "directness", type: "offers" }, { key: "healing_hands", type: "offers" }, { key: "cuts_through_confusion", type: "latent" }, { key: "sharp_tongued", type: "latent" }],
  kawak: [{ key: "gathers_people", type: "offers" }, { key: "nurturing", type: "offers" }, { key: "resilience", type: "latent" }, { key: "takes_on_everyones_problems", type: "latent" }],
  ajpu: [{ key: "courage", type: "offers" }, { key: "determination", type: "offers" }, { key: "warmth", type: "latent" }, { key: "needs_to_win", type: "latent" }],
};

// ── Numbers (13): how strongly the day sign comes through. Latent. ─────────
// Daykeepers read 1-3 as gentle, 10-13 as strong, the rest as the middle.
export const MAYAN_TONE_TAGS = {
  1: [{ key: "gentle_expression", type: "latent" }],
  2: [{ key: "gentle_expression", type: "latent" }],
  3: [{ key: "gentle_expression", type: "latent" }],
  4: [{ key: "balanced_expression", type: "latent" }],
  5: [{ key: "balanced_expression", type: "latent" }],
  6: [{ key: "balanced_expression", type: "latent" }],
  7: [{ key: "balanced_expression", type: "latent" }],
  8: [{ key: "balanced_expression", type: "latent" }],
  9: [{ key: "balanced_expression", type: "latent" }],
  10: [{ key: "strong_expression", type: "latent" }],
  11: [{ key: "strong_expression", type: "latent" }],
  12: [{ key: "strong_expression", type: "latent" }],
  13: [{ key: "strong_expression", type: "latent" }],
};

/* Collect deduped tag rows for a computed Mayan chart.
   mayanData = { daySign:{key}, tone:{number} }
   Dedup by tag_key|tag_type for the saveTags upsert. */
export function collectMayanTags(mayanData) {
  if (!mayanData) return [];
  const { daySign, tone } = mayanData;

  const layers = [
    daySign && MAYAN_DAY_SIGN_TAGS[daySign.key]
      ? { tags: MAYAN_DAY_SIGN_TAGS[daySign.key], value: daySign.key }
      : null,
    tone && MAYAN_TONE_TAGS[tone.number]
      ? { tags: MAYAN_TONE_TAGS[tone.number], value: String(tone.number) }
      : null,
  ].filter(Boolean);

  const seen = new Map();
  for (const layer of layers) {
    for (const t of layer.tags) {
      const k = `${t.key}|${t.type}`;
      if (!seen.has(k)) {
        seen.set(k, {
          tag_key: t.key,
          tag_value: layer.value,
          tag_type: t.type,
          source_module: "star_chart_mayan",
        });
      }
    }
  }
  return Array.from(seen.values());
}
